
import { RevAvatar } from '@ds/components/Avatar'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevLink } from '@ds/components/Link'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'
import { RevToast } from '@ds/components/Toast'
import { IconAvatar } from '@ds/icons/IconAvatar'
import { IconSparkles } from '@ds/icons/IconSparkles'
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import { TOAST_REVIEW_TRANSLATION } from '@config/constants/toasts'
import {
  getDefaultLangFromCountry,
  isJapaneseCharacter,
  isKoreanCharacter,
} from '@core/helpers/countries'
import { formatDateToLongDate } from '@core/helpers/date'
import { createObserver } from '@core/helpers/vue-Intersection-observer'
import { fetchTranslatedReview } from '@http/endpoints'
import { trackClick, trackReviewImpression } from '@tracking/events'

import CharacteristicInlineBlock from '../Page/components/CharacteristicInlineBlock'

import { AVATAR_VARIANTS, MAX_COMMENT_LENGTH } from './UserComment.constants'
import translations from './UserComment.translations'

export default {
  components: {
    CharacteristicInlineBlock,
    IconSparkles,
    RevAvatar,
    RevButtonBase,
    RevLink,
    RevRating,
    RevTag,
    RevToast,
  },
  props: {
    trackingInfo: {
      type: Object,
      default: () => ({}),
    },
    firstName: {
      type: String,
      required: true,
    },
    lastName: {
      type: String,
      required: true,
    },
    date: {
      type: String,
      required: true,
    },
    purchaseDate: {
      type: String,
      default: '',
    },
    comment: {
      type: String,
      required: true,
    },
    score: {
      type: Number,
      required: true,
    },
    position: {
      type: Number,
      required: true,
    },
    product: {
      type: Object,
      default: () => ({}),
    },
    avatar: {
      type: String,
      default: () =>
        AVATAR_VARIANTS[Math.floor(Math.random() * AVATAR_VARIANTS.length)],
    },
    ratingsSize: {
      type: String,
      default: 'medium',
    },
    characteristics: {
      type: Array,
      default: () => [],
    },
    sourceCountry: {
      type: String,
      required: true,
    },
    reviewId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      cut: true,
      flags: {},
      isTranslated: false,
      translatedText: '',
      isToastOpened: false,
    }
  },
  async fetch() {
    if (this.shouldAutoTranslate) {
      try {
        await this.fetchTranslation()
      } catch {
        // fails silently
      }
    }
  },
  computed: {
    ...mapGetters({
      experiments: 'flags/getExperiments',
      country: 'config/country',
      locale: 'config/locale',
      lang: 'config/lang',
    }),
    translations: () => translations,
    TOAST_REVIEW_TRANSLATION: () => TOAST_REVIEW_TRANSLATION,
    shouldAutoTranslate() {
      return (
        this.isTranslatable &&
        this.experiments.autoTranslateReviews === 'withAutoTranslation'
      )
    },
    reviewTrackingInfo() {
      return {
        ...this.trackingInfo,
        reviewPosition: this.position,
        reviewScore: this.score,
        pageType: this.$route.name,
        isReviewFromCurrentCountry: this.isReviewFromCurrentCountry,
        sourceCountry: this.sourceCountry,
        isReviewTranslated: this.isTranslated,
      }
    },
    trackingZone() {
      return `review_position_${this.position}`
    },
    isReviewFromCurrentCountry() {
      return this.sourceCountry === this.country
    },
    isTranslatable() {
      return (
        this.$config.KILL_REVIEW_TRANSLATION === false &&
        !this.isReviewFromCurrentCountry
      )
    },
    language() {
      if (!this.isTranslatable) {
        return this.locale
      }

      const lang = this.isTranslated
        ? getDefaultLangFromCountry(this.sourceCountry)
        : this.lang

      return this.$t.locale(lang)
    },
    displayedDate() {
      return this.$t(translations.reviewDate, {
        country: this.$t.country(this.sourceCountry),
        date: formatDateToLongDate(this.$t, this.date),
      })
    },
    displayedPurchaseDate() {
      return this.$t(translations.purchaseDate, {
        date: formatDateToLongDate(this.$t, this.purchaseDate),
      })
    },
    currentText() {
      return this.isTranslated ? this.translatedText : this.comment
    },
    isCommentLong() {
      return this.currentText.length > MAX_COMMENT_LENGTH
    },
    displayedText() {
      if (this.isCommentLong && this.cut) {
        return `${this.currentText.substring(0, MAX_COMMENT_LENGTH)} ...`
      }

      return this.currentText
    },
    toggleTranslationLabel() {
      return this.isTranslated
        ? this.$t(translations.seeOriginalText)
        : this.$t(translations.translateTextTo, { lang: this.language })
    },
    toggleTextLabel() {
      return this.cut
        ? this.$t(translations.showMore)
        : this.$t(translations.showLess)
    },
    name() {
      return `${this.firstName} ${this.lastName}`
    },
    hasProductInfo() {
      return !isEmpty(this.product)
    },
    hasCharacteristics() {
      return !isEmpty(this.characteristics)
    },
    hasPurchaseDate() {
      return !isEmpty(this.purchaseDate)
    },
    hasJapaneseOrKoreanFirstName() {
      const character = this.firstName.charAt(0)
      const isJapanese = isJapaneseCharacter(character)
      const isKorean = isKoreanCharacter(character)

      return isJapanese || isKorean
    },

    // Using a japanese or a korean character
    // does not make sense as an avatar.
    // We're using an avatar icon instead.
    iconAvatar() {
      if (this.hasJapaneseOrKoreanFirstName) {
        return IconAvatar
      }

      return undefined
    },
  },

  destroyed() {
    if (typeof this.unobserve === 'function') {
      this.unobserve()
    }
  },

  mounted() {
    this.watchReviewInViewport()
  },

  methods: {
    watchReviewInViewport() {
      this.unobserve = createObserver(this.$el, () => {
        trackReviewImpression(this.reviewTrackingInfo)
      })
    },

    toggleText() {
      trackClick({
        name: this.cut ? 'see_more' : 'see_less',
        zone: this.trackingZone,
        page_type: this.$route.name,
      })
      this.cut = !this.cut
    },

    closeToast() {
      this.isToastOpened = false
    },

    async fetchTranslation() {
      const { payload } = await this.$store.dispatch('http/request', {
        request: fetchTranslatedReview,
        pathParams: { reviewId: this.reviewId },
        queryParams: { targetLanguage: this.locale },
      })

      this.isTranslated = true
      this.translatedText = payload.comment
    },

    async toggleTranslation() {
      trackClick({
        name: this.isTranslated ? 'see_original' : 'translate',
        zone: this.trackingZone,
        page_type: this.$route.name,
      })

      if (this.isTranslated || !isEmpty(this.translatedText)) {
        this.isTranslated = !this.isTranslated
        this.cut = false
      } else {
        try {
          await this.fetchTranslation()
          this.cut = false
        } catch {
          this.isToastOpened = true
        }
      }

      this.unobserve()
    },
  },
}
